import React, { useEffect } from 'react';
import CountryPhoneInput from './CountryPhoneInput';

const CourseEnrollModal = ({
  isOpen,
  onClose,
  courseTitle,
  formData,
  setFormData,
  upcomingBatches,
  isSubmitting,
  isSuccess,
  handleEnrollSubmit,
  inline = false
}) => {
  useEffect(() => {
    if (inline) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, inline]);

  const renderFormContent = () => {
    if (isSuccess) {
      return (
        <div style={{ background: 'var(--fy-sage-light)', border: '1px solid var(--fy-sage-border)', color: 'var(--fy-sage-dark)', padding: '20px', borderRadius: 'var(--fy-radius-md)', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 6px', fontWeight: 700 }}>Inquiry Received!</h4>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>Thank you. Our admissions counselor will contact you shortly with batch schedule &amp; installment details.</p>
        </div>
      );
    }

    return (
      <form onSubmit={handleEnrollSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label className="fy-form-label">Student Name *</label>
          <input
            type="text"
            required
            className="fy-form-input"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter your full name"
          />
        </div>
        <div>
          <label className="fy-form-label">Email Address *</label>
          <input
            type="email"
            required
            className="fy-form-input"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="Enter email address"
          />
        </div>
        <div>
          <label className="fy-form-label">Mobile Number *</label>
          <CountryPhoneInput
            countryCode={formData.countryCode}
            onCountryCodeChange={(code) => setFormData({ ...formData, countryCode: code })}
            setCountryCode={(code) => setFormData({ ...formData, countryCode: code })}
            mobileNumber={formData.phone}
            onMobileNumberChange={(num) => setFormData({ ...formData, phone: num })}
            phone={formData.phone}
            setPhone={(num) => setFormData({ ...formData, phone: num })}
          />
        </div>
        <div>
          <label className="fy-form-label">Father's / Guardian's Name *</label>
          <input
            type="text"
            required
            className="fy-form-input"
            value={formData.father_name}
            onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
            placeholder="Enter father's or guardian's name"
          />
        </div>
        <div>
          <label className="fy-form-label">Full Address *</label>
          <input
            type="text"
            required
            className="fy-form-input"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="City, State, Country"
          />
        </div>
        <div>
          <label className="fy-form-label">Select Batch *</label>
          <select
            required
            className="fy-form-input"
            value={formData.batch_id}
            disabled={isSubmitting || upcomingBatches.length === 0}
            onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })}
          >
            <option value="" disabled>
              {upcomingBatches.length === 0 ? 'No upcoming batches available' : '-- Select Upcoming Batch --'}
            </option>
            {upcomingBatches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.batch_name} ({b.batch_timing}) — Starts {b.start_date}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={isSubmitting} className="fy-btn-primary w-100 py-3 mt-2">
          {isSubmitting ? 'Processing...' : 'Proceed to Confirm Enrollment →'}
        </button>
      </form>
    );
  };

  if (inline) {
    return (
      <div
        className="pf-enroll-card"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid var(--fy-sage-border, #d1e2d7)',
          boxShadow: '0 8px 24px rgba(22, 78, 63, 0.08)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 24px 16px 24px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #eef3f0',
            position: 'relative',
            flexShrink: 0
          }}
        >
          <h3 style={{ margin: '0 0 6px 0', fontSize: '1.35rem', fontWeight: 800, color: '#1f332c', fontFamily: 'var(--heading-font-family)' }}>
            Enroll In {courseTitle}
          </h3>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#60756c', lineHeight: 1.5 }}>
            Fill out your details below to proceed to registration.
          </p>
        </div>

        {/* Form Body */}
        <div
          style={{
            padding: '20px 24px 28px 24px',
            flex: 1
          }}
        >
          {renderFormContent()}
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target.id === 'courseEnrollModalOverlay') {
      onClose();
    }
  };

  return (
    <div
      id="courseEnrollModalOverlay"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 30, 24, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(10, 35, 25, 0.22)',
          width: '100%',
          maxWidth: '520px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          boxSizing: 'border-box',
          animation: 'pfModalFadeIn 0.22s ease-out'
        }}
      >
        <style>{`
          @keyframes pfModalFadeIn {
            from { opacity: 0; transform: scale(0.96); }
            to { opacity: 1; transform: scale(1); }
          }
          .pf-modal-close-btn:hover {
            background-color: #e8f0ec !important;
            color: #1f332c !important;
          }
        `}</style>

        {/* Fixed Header */}
        <div
          style={{
            padding: '24px 24px 16px 24px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #eef3f0',
            position: 'relative',
            flexShrink: 0,
            zIndex: 2
          }}
        >
          <button
            className="pf-modal-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#f0f5f2',
              color: '#556b62',
              fontSize: '18px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              zIndex: 3
            }}
          >
            &times;
          </button>

          <h3 style={{ margin: '0 0 6px 0', fontSize: '1.35rem', fontWeight: 800, color: '#1f332c', fontFamily: 'var(--heading-font-family)', paddingRight: '36px' }}>
            Enroll In {courseTitle}
          </h3>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#60756c', lineHeight: 1.5, paddingRight: '20px' }}>
            Fill out your details below to proceed to registration.
          </p>
        </div>

        {/* Scrollable Form Body */}
        <div
          style={{
            padding: '20px 24px 28px 24px',
            overflowY: 'auto',
            flex: 1,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {renderFormContent()}
        </div>
      </div>
    </div>
  );
};

export default CourseEnrollModal;
